import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import mapboxgl from "mapbox-gl";
import "mapbox-gl/dist/mapbox-gl.css";
import { createRoot } from "react-dom/client";
import MapPinArt from "./MapPinArt";
import { LocateFixed, Search as SearchIcon } from "lucide-react";

mapboxgl.accessToken = process.env.REACT_APP_MAPBOX_TOKEN || "";

/**
 * HGMap — Phase 4/5. Interactive Mapbox GL map with:
 *   • Marker clustering (server-safe: consumes vendors[] the parent already loaded)
 *   • Custom Hidden Gem MapPinArt markers per vendor tier
 *   • Tap-to-select → onSelectVendor(vendor)
 *   • "My location" recenter
 *   • "Search this area" — parent decides what to do with new bounds
 *
 * Props:
 *   vendors      — array of vendor docs with vendor_profile.location_lat/lng
 *   center       — {lat, lng} initial center (falls back to first vendor)
 *   selectedId   — user_id of the currently-selected vendor (highlight)
 *   onSelectVendor — (vendor) => void
 *   onSearchArea — ({lat, lng, radius_km}) => void, called when user taps "Search this area"
 *   showMyLocation — bool, if true draws customer's approximate location pin
 *   myLocation   — {lat, lng} | null
 */
export default function HGMap({
  vendors = [],
  center,
  selectedId,
  onSelectVendor,
  onSearchArea,
  showMyLocation = true,
  myLocation = null,
}) {
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const markersRef = useRef(new Map()); // user_id → {marker, root}
  const myLocMarkerRef = useRef(null);
  const [ready, setReady] = useState(false);
  const [showSearchHere, setShowSearchHere] = useState(false);

  const initialCenter = useMemo(() => {
    if (center && center.lat != null && center.lng != null) return [center.lng, center.lat];
    const first = vendors.find((v) => v.vendor_profile?.location_lat != null);
    if (first) return [first.vendor_profile.location_lng, first.vendor_profile.location_lat];
    return [-73.9442, 40.6782]; // Brooklyn default
  }, [center, vendors]);

  // Boot map
  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;
    if (!mapboxgl.accessToken) return;
    const map = new mapboxgl.Map({
      container: containerRef.current,
      style: "mapbox://styles/mapbox/dark-v11",
      center: initialCenter,
      zoom: 11,
      attributionControl: false,
    });
    map.addControl(new mapboxgl.AttributionControl({ compact: true }), "bottom-left");
    map.addControl(new mapboxgl.NavigationControl({ showCompass: false, visualizePitch: false }), "top-right");

    map.on("load", () => {
      setReady(true);
      map.addSource("hg-vendors", {
        type: "geojson",
        data: { type: "FeatureCollection", features: [] },
        cluster: true,
        clusterMaxZoom: 13,
        clusterRadius: 55,
      });
      // Cluster bubble
      map.addLayer({
        id: "hg-cluster",
        type: "circle",
        source: "hg-vendors",
        filter: ["has", "point_count"],
        paint: {
          "circle-color": "#F472B6",
          "circle-opacity": 0.85,
          "circle-radius": ["step", ["get", "point_count"], 20, 10, 26, 25, 32],
          "circle-stroke-color": "#FCE7F3",
          "circle-stroke-width": 2,
        },
      });
      map.addLayer({
        id: "hg-cluster-count",
        type: "symbol",
        source: "hg-vendors",
        filter: ["has", "point_count"],
        layout: {
          "text-field": ["get", "point_count_abbreviated"],
          "text-font": ["DIN Offc Pro Medium", "Arial Unicode MS Bold"],
          "text-size": 14,
        },
        paint: { "text-color": "#0a0a0a" },
      });
      map.on("click", "hg-cluster", (e) => {
        const feats = map.queryRenderedFeatures(e.point, { layers: ["hg-cluster"] });
        const clusterId = feats[0]?.properties?.cluster_id;
        map.getSource("hg-vendors").getClusterExpansionZoom(clusterId, (err, zoom) => {
          if (err) return;
          map.easeTo({ center: feats[0].geometry.coordinates, zoom });
        });
      });
    });

    map.on("moveend", () => {
      // Only show once the user actually pans / zooms significantly
      setShowSearchHere(true);
    });

    mapRef.current = map;
    return () => {
      map.remove();
      mapRef.current = null;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Sync vendors → GeoJSON source (for clustering) + custom DOM markers for
  // the top ~40 nearest so premium pin artwork is visible without hurting perf.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !ready) return;
    const withCoords = vendors.filter((v) => {
      const vp = v.vendor_profile || {};
      return vp.location_lat != null && vp.location_lng != null;
    });

    const features = withCoords.map((v) => ({
      type: "Feature",
      geometry: { type: "Point", coordinates: [v.vendor_profile.location_lng, v.vendor_profile.location_lat] },
      properties: { user_id: v.user_id },
    }));
    const src = map.getSource("hg-vendors");
    if (src) src.setData({ type: "FeatureCollection", features });

    // Sync HTML markers — reuse where possible so React roots don't churn.
    const nextIds = new Set(withCoords.map((v) => v.user_id));
    for (const [uid, { marker }] of markersRef.current) {
      if (!nextIds.has(uid)) {
        marker.remove();
        markersRef.current.delete(uid);
      }
    }
    withCoords.slice(0, 60).forEach((v) => {
      const uid = v.user_id;
      const rep = v.reputation || {};
      const tier = rep.tier || v.equipped_map_pin || "white";
      const isSelected = uid === selectedId;
      let entry = markersRef.current.get(uid);
      if (!entry) {
        const el = document.createElement("div");
        el.style.cursor = "pointer";
        el.style.zIndex = isSelected ? "5" : "1";
        const root = createRoot(el);
        el.addEventListener("click", (e) => {
          e.stopPropagation();
          onSelectVendor?.(v);
        });
        const marker = new mapboxgl.Marker({ element: el, anchor: "bottom" })
          .setLngLat([v.vendor_profile.location_lng, v.vendor_profile.location_lat])
          .addTo(map);
        entry = { marker, root, el };
        markersRef.current.set(uid, entry);
      } else {
        entry.marker.setLngLat([v.vendor_profile.location_lng, v.vendor_profile.location_lat]);
      }
      entry.el.setAttribute("data-testid", `map-marker-${uid}`);
      entry.el.style.zIndex = isSelected ? "5" : "1";
      entry.root.render(<MapPinArt tier={tier} size={isSelected ? 56 : 44} selected={isSelected} />);
    });
  }, [vendors, ready, selectedId, onSelectVendor]);

  // My-location marker
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !ready) return;
    if (myLocMarkerRef.current) {
      myLocMarkerRef.current.remove();
      myLocMarkerRef.current = null;
    }
    if (!showMyLocation || !myLocation) return;
    const el = document.createElement("div");
    el.style.width = "20px";
    el.style.height = "20px";
    el.style.borderRadius = "50%";
    el.style.background = "#22D3EE";
    el.style.boxShadow = "0 0 0 4px rgba(34,211,238,0.35), 0 0 10px rgba(34,211,238,0.55)";
    el.style.border = "2px solid #082F49";
    el.setAttribute("data-testid", "map-my-location");
    myLocMarkerRef.current = new mapboxgl.Marker({ element: el })
      .setLngLat([myLocation.lng, myLocation.lat])
      .addTo(map);
  }, [myLocation, showMyLocation, ready]);

  const recenter = useCallback(() => {
    const map = mapRef.current;
    if (!map || !myLocation) return;
    map.easeTo({ center: [myLocation.lng, myLocation.lat], zoom: 12 });
    setShowSearchHere(false);
  }, [myLocation]);

  const searchThisArea = useCallback(() => {
    const map = mapRef.current;
    if (!map) return;
    const c = map.getCenter();
    const b = map.getBounds();
    // Approximate radius = half the diagonal of the visible bounds (km)
    const R = 6371;
    const toRad = (d) => (d * Math.PI) / 180;
    const nw = b.getNorthWest(), se = b.getSouthEast();
    const dLat = toRad(se.lat - nw.lat), dLng = toRad(se.lng - nw.lng);
    const a = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(nw.lat)) * Math.cos(toRad(se.lat)) * Math.sin(dLng / 2) ** 2;
    const km = (R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))) / 2;
    onSearchArea?.({ lat: c.lat, lng: c.lng, radius_km: Math.max(1, Math.min(200, Math.round(km))) });
    setShowSearchHere(false);
  }, [onSearchArea]);

  if (!mapboxgl.accessToken) {
    return (
      <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-8 text-center text-zinc-400 text-sm" data-testid="map-missing-token">
        Map is temporarily disabled — Mapbox token not configured.
      </div>
    );
  }

  return (
    <div className="relative w-full h-full rounded-2xl overflow-hidden border border-white/10" data-testid="hg-map">
      <div ref={containerRef} className="w-full h-full" />
      {showSearchHere && (
        <button
          onClick={searchThisArea}
          className="absolute top-3 left-1/2 -translate-x-1/2 h-9 px-4 rounded-full bg-pink-500 hover:bg-pink-400 text-white text-xs font-black inline-flex items-center gap-1.5 shadow-lg z-[3]"
          data-testid="map-search-this-area"
        >
          <SearchIcon className="w-3.5 h-3.5" /> Search this area
        </button>
      )}
      {myLocation && (
        <button
          onClick={recenter}
          className="absolute bottom-4 right-3 w-11 h-11 rounded-full bg-zinc-900/90 border border-white/10 grid place-items-center text-cyan-300 hover:bg-zinc-900 z-[3]"
          data-testid="map-recenter"
          aria-label="Recenter on my location"
        >
          <LocateFixed className="w-4 h-4" />
        </button>
      )}
    </div>
  );
}
